import { ChangeEvent, memo, useState } from "react";

import AudioAttachment from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Attachment/AudioAttachment";
import EmbedAttachment from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Attachment/EmbedAttachment";
import HTMLAttachment, {
  InsertHTMLTagHandler,
} from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Attachment/HTMLAttachment";
import ImageAttachment from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Attachment/ImageAttachment";
import VideoAttachment from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Attachment/VideoAttachment";
import BoldToggle from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Toolbar/BoldToggle";
import BulletListToggle from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Toolbar/BulletListToggle";
import EmojiPicker from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Toolbar/EmojiPicker";
import HeadingSelector from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Toolbar/HeadingSelector";
import ItalicToggle from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Toolbar/ItalicToggle";
import LinkInsert from "@hl/communities-app/lib/components/ContentFeed/TextEditor/plugins/Toolbar/LinkInsert";
import {
  SelectedElement,
  Attachment,
  AttachmentType,
  SupportedMediaType,
} from "@hl/communities-app/lib/components/ContentFeed/TextEditor/types";
import { Group, useMantineTheme } from "@mantine/core";

import EmbedTagSelectModal from "~/features/pages/Editor/components/EmbedTagSelectModal";

import * as styles from "./styles";

interface ToolbarPluginProps {
  selectedElement: SelectedElement;
  blockType: string;
  isEditable: boolean;
  //TODO: Use type from Editor types file
  handleFileSelected: (
    mediaType: SupportedMediaType,
    type: AttachmentType,
    callBack: (attachment: Attachment, fileName: string) => void
  ) => (event: ChangeEvent<HTMLInputElement>) => void;
  anchorElem?: HTMLElement;
  disableHTMLPlugin: boolean;
  showHTMLTagSelectorModal: (insertHandler: InsertHTMLTagHandler) => void;
  closeHTMLTagSelectorModal: () => void;
}

const ToolbarPlugin = ({
  selectedElement,
  blockType,
  isEditable,
  handleFileSelected,
  anchorElem,
  disableHTMLPlugin,
  showHTMLTagSelectorModal,
  closeHTMLTagSelectorModal,
}: ToolbarPluginProps) => {
  const theme = useMantineTheme();
  const [showEditorEmbedModal, setShowEditorEmbedModal] =
    useState<boolean>(false);

  if (!isEditable) return null;

  const { classes } = styles.useStyles();

  const showEmbedTagSelectorModal = () => {
    setShowEditorEmbedModal(true);
  };

  const isDark = theme.colorScheme === "dark";

  return (
    <Group position="apart" className={classes.toolbar}>
      <div css={styles.leftBar}>
        <HeadingSelector blockType={blockType} dark={isDark} />
        <div css={styles.pluginGroup}>
          <BoldToggle selectedElement={selectedElement} dark={isDark} />
          <ItalicToggle selectedElement={selectedElement} />
          <BulletListToggle blockType={blockType} />
          <EmojiPicker />
        </div>
      </div>
      <div css={styles.rightBar}>
        <LinkInsert selectedElement={selectedElement} anchorElem={anchorElem} />
        <ImageAttachment handleFileSelected={handleFileSelected} />
        <AudioAttachment handleFileSelected={handleFileSelected} />
        <VideoAttachment handleFileSelected={handleFileSelected} />
        <EmbedAttachment
          onEmbedButtonClickHandler={showEmbedTagSelectorModal}
          postInsertHandler={() => setShowEditorEmbedModal(false)}
        />
        {!disableHTMLPlugin && (
          <HTMLAttachment
            onHTMLButtonClickHandler={showHTMLTagSelectorModal}
            postInsertHandler={closeHTMLTagSelectorModal}
          />
        )}
      </div>
      <EmbedTagSelectModal
        opened={showEditorEmbedModal}
        onClose={() => setShowEditorEmbedModal(false)}
      />
    </Group>
  );
};

export default memo(ToolbarPlugin);
